import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GRADES, gradeLabel, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, ClipboardCheck, ArrowRight, Check, FileCheck2, FileDown, Calendar, Power, Trash2, Sparkles, Link2, X } from "lucide-react";

function EvidenceSuggestDialog({ audit_id, item, onAttached }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [attached, setAttached] = useState(new Set(item.evidence_ids || []));
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/audits/${audit_id}/items/${item.id}/suggested-evidence`); setItems(data.suggestions); }
    finally { setBusy(false); }
  };
  const toggle = async (id) => {
    const s = new Set(attached); s.has(id) ? s.delete(id) : s.add(id);
    setAttached(s);
    await api.patch(`/audits/${audit_id}/items/${item.id}`, { evidence_ids: Array.from(s) });
    onAttached && onAttached(Array.from(s));
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DialogTrigger asChild>
        <Button data-testid={`suggest-ev-${item.id}`} type="button" variant="outline" size="sm" className="mt-2 h-7 text-xs border-indigo-300 text-indigo-800 hover:bg-indigo-50">
          <Sparkles className="h-3 w-3 mr-1" />Suggest evidence
          {(item.evidence_ids || []).length > 0 && <span className="ml-2 text-emerald-800 font-mono">{(item.evidence_ids || []).length}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>Evidence for this audit question</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 mb-2">Ranked by theme match and sub-area keyword overlap. Selecting attaches evidence to this audit item.</p>
        <div className="max-h-72 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
          {busy && <div className="p-4 text-sm text-slate-500">Loading…</div>}
          {!busy && items.length === 0 && <div className="p-4 text-sm text-slate-500">No evidence found for this theme yet — add some in the Evidence library.</div>}
          {items.map((e) => {
            const on = attached.has(e.id);
            return (
              <button key={e.id} data-testid={`attach-ev-${e.id}`} type="button" onClick={() => toggle(e.id)} className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                {on ? <Check className="h-4 w-4 text-indigo-800 mt-0.5" /> : <Link2 className="h-4 w-4 text-slate-400 mt-0.5" />}
                <div>
                  <div className="font-medium text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500">{e.source_type} · by {e.created_by_name || "—"}{(e.tags || []).length > 0 && ` · ${e.tags.join(", ")}`}</div>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ item, onSave, audit_id, onAttached }) {
  const [grade, setGrade] = useState(item.grade || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [rec, setRec] = useState(item.recommendation || "");
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await onSave({ grade: grade || null, notes, recommendation: rec }); setSaving(false); };
  const setGradeInstant = async (g) => { setGrade(g); setSaving(true); await onSave({ grade: g, notes, recommendation: rec }); setSaving(false); };
  return (
    <div className="border-b border-slate-100 p-4" data-testid={`audit-item-${item.id}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">{item.sub_area_title}</div>
      <div className="font-medium text-slate-900">{item.question}</div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-5 gap-1">
        {GRADES.map((g) => (
          <button data-testid={`audit-grade-${item.id}-${g.key}`} key={g.key} type="button" onClick={() => setGradeInstant(g.key)}
            className={`text-[11px] font-semibold rounded-md px-2 py-1.5 border transition-colors ${grade === g.key ? `grade-${g.key} ring-2 ring-offset-1 ring-indigo-400` : "bg-white border-slate-200 hover:bg-slate-50 text-slate-600"}`}>
            {g.label}
          </button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Textarea data-testid={`audit-notes-${item.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={save} rows={2} placeholder="Findings / evidence observed…" className="text-sm" />
        <Textarea data-testid={`audit-rec-${item.id}`} value={rec} onChange={(e) => setRec(e.target.value)} onBlur={save} rows={2} placeholder="Recommendation (becomes QIP action if graded low)…" className="text-sm" />
      </div>
      <EvidenceSuggestDialog audit_id={audit_id} item={item} onAttached={(ids) => onAttached && onAttached(item.id, ids)} />
      {saving && <div className="text-[11px] text-slate-400 mt-1">Saving…</div>}
    </div>
  );
}

export default function Audit() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { audit_id } = useParams();
  const [audits, setAudits] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [audit, setAudit] = useState(null);
  const [themes, setThemes] = useState([]);
  const [currentTheme, setCurrentTheme] = useState("safeguarding");
  const [open, setOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ provider_name: "", frequency: "quarterly", auditor_name: user?.name || "", active: true });
  const [form, setForm] = useState({ provider_name: "", auditor_name: user?.name || "", notes: "" });
  const [completing, setCompleting] = useState(false);

  const loadList = async () => {
    const [aa, ss] = await Promise.all([api.get("/audits"), api.get("/audit-schedules")]);
    setAudits(aa.data); setSchedules(ss.data);
  };
  const loadOne = async () => {
    if (!audit_id) return;
    const [t, a] = await Promise.all([api.get("/themes"), api.get(`/audits/${audit_id}`)]);
    setThemes(t.data); setAudit(a.data);
    if (t.data.length && !t.data.find((x) => x.key === currentTheme)) setCurrentTheme(t.data[0].key);
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadOne(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [audit_id]);

  const createAudit = async () => {
    try { const { data } = await api.post("/audits", form);
      toast.success("Audit created"); setOpen(false); loadList(); nav(`/audit/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveItem = async (item_id, patch) => {
    try {
      const { data } = await api.patch(`/audits/${audit_id}/items/${item_id}`, patch);
      setAudit((prev) => ({ ...prev, items: prev.items.map((it) => it.id === item_id ? { ...it, ...data } : it) }));
    } catch (e) { toast.error("Save failed"); }
  };

  const complete = async () => {
    if (!confirm("Complete audit? This will update theme grades and transfer low-graded items to the QIP tracker as red actions.")) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/audits/${audit_id}/complete`, { transfer_to_qip: true, update_theme_grades: true });
      toast.success(`Audit complete · ${data.actions_created} QIP actions created`);
      loadOne();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCompleting(false); }
  };

  const createSchedule = async () => {
    try { await api.post("/audit-schedules", scheduleForm); toast.success("Schedule created"); setScheduleOpen(false); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleSchedule = async (sid) => { await api.post(`/audit-schedules/${sid}/toggle`); loadList(); };
  const deleteSchedule = async (sid) => { if (!confirm("Delete schedule?")) return; await api.delete(`/audit-schedules/${sid}`); loadList(); };
  const runSchedulesNow = async () => {
    try { const { data } = await api.post("/audit-schedules/run-now"); toast.success(`${data.opened.length} audit(s) opened`); loadList(); }
    catch (e) { toast.error("Failed"); }
  };

  const deleteAudit = async (a) => {
    const purge = confirm(`Delete audit "${a.provider_name}"?\n\nClick OK to also delete the ${a.actions_created || 0} QIP actions this audit created, or Cancel this dialog and choose again.`);
    // Second confirmation for safety
    if (!confirm(`Really delete "${a.provider_name}"${purge && a.actions_created ? " AND its " + a.actions_created + " QIP action(s)" : ""}?`)) return;
    try {
      await api.delete(`/audits/${a.id}${purge && a.actions_created ? "?purge_generated_actions=true" : ""}`);
      toast.success("Audit deleted");
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadAuditPdf = () => {
    const token = localStorage.getItem("qip_token");
    window.open(`${api.defaults.baseURL}/audits/${audit_id}/pdf?auth=${token}`, "_blank");
  };

  // Audit list mode
  if (!audit_id) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Audit</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Ofsted framework audit</h1>
            <p className="text-slate-600 mt-1">Run a comprehensive audit against the Ofsted FE & Skills toolkit. Findings flow straight into your QIP tracker with matching grades.</p>
          </div>
          {canManage(user?.role) && (
            <div className="flex gap-2 flex-wrap">
              <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <DialogTrigger asChild><Button data-testid="new-schedule-btn" variant="outline"><Calendar className="h-4 w-4 mr-1.5" />Recurring audit</Button></DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader><DialogTitle>Schedule a recurring audit</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Business / provider name</Label><Input data-testid="sched-provider" value={scheduleForm.provider_name} onChange={(e) => setScheduleForm({ ...scheduleForm, provider_name: e.target.value })} /></div>
                    <div><Label>Frequency</Label>
                      <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm({ ...scheduleForm, frequency: v })}>
                        <SelectTrigger data-testid="sched-freq"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly (every 90 days)</SelectItem><SelectItem value="annual">Annual</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Default lead auditor</Label><Input value={scheduleForm.auditor_name} onChange={(e) => setScheduleForm({ ...scheduleForm, auditor_name: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button data-testid="save-schedule-btn" onClick={createSchedule} disabled={!scheduleForm.provider_name} className="bg-indigo-900 hover:bg-indigo-950 text-white">Create schedule</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="new-audit-btn" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Plus className="h-4 w-4 mr-1.5" />Start new audit</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Start a new audit</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Business / provider name</Label><Input data-testid="audit-provider" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} placeholder="e.g. Riverside Skills College" /></div>
                  <div><Label>Lead auditor</Label><Input data-testid="audit-auditor" value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} /></div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Scope, method, dates…" /></div>
                </div>
                <DialogFooter><Button data-testid="create-audit-btn" onClick={createAudit} disabled={!form.provider_name} className="bg-indigo-900 hover:bg-indigo-950 text-white">Create audit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {schedules.length > 0 && (
          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-bold text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>Recurring audits</h2>
                <p className="text-xs text-slate-500">Each schedule auto-opens a fresh audit on its next-run date · daily 06:00 UTC scan</p>
              </div>
              {canManage(user?.role) && <Button data-testid="run-schedules-btn" size="sm" variant="outline" onClick={runSchedulesNow}>Run due now</Button>}
            </div>
            <div className="space-y-2">
              {schedules.map((s) => (
                <div key={s.id} className="flex items-center gap-3 flex-wrap border border-slate-200 rounded-md p-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-slate-900">{s.provider_name}</div>
                    <div className="text-xs text-slate-500">{s.frequency} · lead auditor {s.auditor_name || "—"}</div>
                  </div>
                  <div className="text-xs text-slate-600">Next run <b>{s.next_run_at?.slice(0, 10)}</b></div>
                  {s.last_run_at && <div className="text-xs text-slate-500">Last {s.last_run_at.slice(0, 10)}</div>}
                  <Badge className={s.active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}>{s.active ? "Active" : "Paused"}</Badge>
                  {canManage(user?.role) && (
                    <>
                      <Button data-testid={`toggle-sched-${s.id}`} size="icon" variant="ghost" onClick={() => toggleSchedule(s.id)}><Power className="h-3.5 w-3.5" /></Button>
                      <Button data-testid={`del-sched-${s.id}`} size="icon" variant="ghost" onClick={() => deleteSchedule(s.id)}><Trash2 className="h-3.5 w-3.5 text-slate-500" /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {audits.length === 0 && <Card className="p-8 text-center text-slate-500 md:col-span-2 xl:col-span-3">No audits yet. Start your first Ofsted-framework audit.</Card>}
          {audits.map((a) => {
            const done = a.items.filter((i) => i.grade).length;
            const total = a.items.length;
            return (
              <Card key={a.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow duration-200" data-testid={`audit-card-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{new Date(a.created_at).toLocaleDateString("en-GB")}</div>
                    <div className="font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{a.provider_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Auditor: {a.auditor_name}</div>
                  </div>
                  {a.status === "complete" ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><Check className="h-3 w-3 mr-1" />Complete</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">In progress</Badge>}
                </div>
                <div className="mt-3 text-xs text-slate-500">{done} / {total} questions graded</div>
                <Progress value={(done / Math.max(total, 1)) * 100} className="h-2 mt-1" />
                {a.actions_created > 0 && <div className="mt-2 text-xs text-emerald-800">→ {a.actions_created} QIP actions created</div>}
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => nav(`/audit/${a.id}`)} data-testid={`open-audit-${a.id}`}>Open <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                  {canManage(user?.role) && (
                    <Button variant="ghost" size="sm" onClick={() => deleteAudit(a)} data-testid={`del-audit-${a.id}`} className="ml-auto text-slate-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Audit detail mode
  if (!audit) return <div className="text-slate-500">Loading audit…</div>;
  const themeItems = audit.items.filter((i) => i.theme_key === currentTheme);
  const overallDone = audit.items.filter((i) => i.grade).length;
  const overallTotal = audit.items.length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Audit · {audit.provider_name}</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Ofsted framework audit</h1>
          <p className="text-slate-600 mt-1 text-sm">Lead auditor: {audit.auditor_name} · Started {new Date(audit.created_at).toLocaleDateString("en-GB")} · {overallDone}/{overallTotal} graded</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/audit")}>Back to audits</Button>
          <Button variant="outline" onClick={downloadAuditPdf} data-testid="audit-pdf-btn"><FileDown className="h-4 w-4 mr-1.5" />Download audit PDF</Button>
          {audit.status !== "complete" && canManage(user?.role) && (
            <Button data-testid="complete-audit-btn" onClick={complete} disabled={completing || overallDone === 0} className="bg-emerald-700 hover:bg-emerald-800 text-white"><FileCheck2 className="h-4 w-4 mr-1.5" />{completing ? "Transferring…" : "Complete & transfer to QIP"}</Button>
          )}
        </div>
      </div>

      <Card className="p-4 border-slate-200">
        <Progress value={(overallDone / Math.max(overallTotal, 1)) * 100} className="h-2" />
      </Card>

      <Tabs value={currentTheme} onValueChange={setCurrentTheme}>
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap p-1">
          {themes.map((t) => {
            const c = audit.items.filter((i) => i.theme_key === t.key);
            const d = c.filter((i) => i.grade).length;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`audit-tab-${t.key}`} className="text-xs data-[state=active]:bg-indigo-900 data-[state=active]:text-white">
                {t.title} <span className="ml-1 font-mono text-[10px] opacity-70">{d}/{c.length}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <Card className="border-slate-200 overflow-hidden">
        {themeItems.map((it) => (<Row key={it.id} item={it} audit_id={audit_id} onSave={(patch) => saveItem(it.id, patch)} onAttached={(iid, ids) => setAudit((prev) => ({ ...prev, items: prev.items.map((x) => x.id === iid ? { ...x, evidence_ids: ids } : x) }))} />))}
      </Card>

      {audit.status === "complete" && (
        <Card className="p-5 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold"><ClipboardCheck className="h-5 w-5" />Audit complete</div>
          <div className="text-sm text-emerald-800 mt-2">Theme grades updated: {Object.entries(audit.theme_grades_applied || {}).map(([k, v]) => `${k}: ${gradeLabel(v)}`).join(" · ")}</div>
          <div className="text-sm text-emerald-800 mt-1">{audit.actions_created} QIP actions created from findings graded Urgent Improvement or Needs Attention.</div>
        </Card>
      )}
    </div>
  );
}
